<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type { StrictDeepPick } from 'ts-essentials';
import type { ReadonlyDeep } from 'type-fest';
import type {
  InitialData as EditTimestampsDialogFormInitialData,
  ValidData as EditTimestampsDialogFormValidData,
} from '@/components/dialogForms/EditTimestampsDialogForm.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, useTemplateRef } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import EditTimestampsDialogForm from '@/components/dialogForms/EditTimestampsDialogForm.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';
import * as dateHelpers from '@/helpers/date';

export interface Props {
  isConfirmLoading?: boolean;
  job?: ReadonlyDeep<
    StrictDeepPick<
      openapi.DespatchJobsContentOpenapi[0],
      {
        id: never;
        rockhopper_id: never;
        rockhopper_date: never;
        stops: {
          stop_number: never;
          arrived_at: never;
          departed_at: never;
          required_at: never;
          time_earliest: never;
          time_latest: never;
          assigned_at: never;
        }[];
      }
    >
  >;
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  isConfirmLoading: false,
  job: undefined,
});
const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: SubmitEventPromise | null | undefined,
    formData: EditTimestampsDialogFormValidData,
    props: DefProps,
  ];
}>();

function handleCancelClick(event: Event): void {
  // also it should be wrapped with <v-dialog :persistent="isConfirmLoading"
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleSubmit(
  event: SubmitEventPromise | null | undefined,
  formData: EditTimestampsDialogFormValidData,
): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, formData, props);
}

const mainEl = useTemplateRef('main');

function getSubmitBtn(): HTMLInputElement | null {
  if (!mainEl.value) return null;
  return mainEl.value.querySelector('input[type=submit]');
}

function handleConfirmClick(event: Event): void {
  const submitBtn = getSubmitBtn();
  if (!submitBtn) return;
  submitBtn.click();
}

const initialData = computed<EditTimestampsDialogFormInitialData>(() => {
  return {
    timestamps: props.job?.stops.map((stop) => {
      return {
        // departed: dateHelpers.formatDateTimeToDateTimeFieldsetValue(stop.required_at),
        // departed: dateHelpers.formatDateTimeToDateTimeFieldsetValue(stop.time_earliest),

        requested: dateHelpers.formatDateTimeToDateTimeFieldsetValue(), // TODO
        latest: dateHelpers.formatDateTimeToDateTimeFieldsetValue(
          stop.time_latest,
        ),
        estimated: dateHelpers.formatDateTimeToDateTimeFieldsetValue(), // TODO
        arrived: dateHelpers.formatDateTimeToDateTimeFieldsetValue(
          stop.arrived_at,
        ),
        departed: dateHelpers.formatDateTimeToDateTimeFieldsetValue(
          stop.departed_at,
        ),
        pod: dateHelpers.formatDateTimeToDateTimeFieldsetValue(), // TODO
        assigned: dateHelpers.formatDateTimeToDateTimeFieldsetValue(
          stop.assigned_at,
        ),
      };
    }),
  };
});
</script>

<template>
  <PageDialogWrapper
    :class="$style.editTimestampsDialog"
    data-testid="editTimestampsDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        Edit Timestamps Job #{{ props.job?.rockhopper_id }}
      </ReportGenerateDialogHeader>
    </template>
    <section ref="main" :class="$style.main">
      <EditTimestampsDialogForm
        :initial-data="initialData"
        :loading="props.isConfirmLoading"
        @submit="handleSubmit"
      />
    </section>
    <template #footer>
      <menu :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :class="$style.btn"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-check"
            :class="$style.btn"
            :loading="props.isConfirmLoading"
            @click="handleConfirmClick"
          >
            Save changes
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:math';
@use '@/styles/colors';
@use '@/styles/variables';

.editTimestampsDialog {
  width: 870px;
}
  .main {
    margin: 0 - #{variables.$pageDialogWindowPaddingX};
  }
  .dialogFooterMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: right;
  }
    .dialogFooterMenuListItem {
      display: inline;
    }
    .dialogFooterMenuListItem + .dialogFooterMenuListItem {
      margin-left: 1rem;
    }
      .btn {
      }
</style>
